import React from 'react';
import { Plus } from 'lucide-react';
import { AppSelect } from './AppSelect';

export type DocumentFolderItem = {
  id: string;
  name: string;
  count: number;
};

type DocumentFolderSidebarProps = {
  folders: DocumentFolderItem[];
  totalCount: number;
  selectedFolderId: string | null;
  onSelectFolder: (folderId: string | null) => void;
  onAddFolder: () => void;
  /** Optional slot rendered in the mobile header row (e.g. help button). */
  mobileTrailing?: React.ReactNode;
  className?: string;
};

export function DocumentFolderSidebar({
  folders,
  totalCount,
  selectedFolderId,
  onSelectFolder,
  onAddFolder,
  mobileTrailing,
  className = '',
}: DocumentFolderSidebarProps) {
  return (
    <>
      {/* Mobile: folder dropdown header */}
      <div className={`md:hidden shrink-0 px-3 py-2 border-b border-app-border bg-app-surface flex items-center gap-2 ${className}`}>
        <AppSelect
          value={selectedFolderId ?? ''}
          onChange={(v) => onSelectFolder(v || null)}
          options={[
            { value: '', label: `Alle Dokumente (${totalCount})` },
            ...folders.map((f) => ({ value: f.id, label: `${f.name} (${f.count})` })),
          ]}
          className="flex-1"
        />
        {mobileTrailing}
        <button
          type="button"
          onClick={onAddFolder}
          className="shrink-0 p-2 rounded-lg text-app-text-subtle hover:bg-app-hover hover:text-app-text"
          aria-label="Ordner hinzufügen"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Desktop: folder sidebar */}
      <aside className={`hidden md:flex w-[272px] shrink-0 flex-col min-h-0 overflow-hidden border-r border-app-border bg-app-surface ${className}`}>
        <div className="app-scrollbar flex-1 overflow-y-auto overscroll-contain px-3 py-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-xs font-medium text-app-text-muted">Ordner</span>
            <button
              type="button"
              onClick={onAddFolder}
              className="p-1 rounded text-app-text-subtle hover:text-app-text hover:bg-app-hover"
              aria-label="Ordner hinzufügen"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-0.5">
            <button
              type="button"
              onClick={() => onSelectFolder(null)}
              className={`w-full flex items-center gap-1 min-w-0 rounded-md px-2 py-2 text-sm ${
                selectedFolderId === null ? 'bg-app-surface-muted' : 'hover:bg-app-hover'
              }`}
            >
              <span className="flex-1 min-w-0 truncate text-app-text text-left">Alle Dokumente</span>
              <span className="shrink-0 text-xs text-app-text-subtle tabular-nums w-8 text-right">{totalCount}</span>
            </button>
            {folders.map((folder) => (
              <button
                type="button"
                key={folder.id}
                onClick={() => onSelectFolder(folder.id)}
                className={`w-full flex items-center gap-1 min-w-0 rounded-md px-2 py-2 text-sm ${
                  selectedFolderId === folder.id ? 'bg-app-surface-muted' : 'hover:bg-app-hover'
                }`}
              >
                <span className="flex-1 min-w-0 truncate text-app-text text-left">{folder.name}</span>
                <span className="shrink-0 text-xs text-app-text-subtle tabular-nums w-8 text-right">{folder.count}</span>
              </button>
            ))}
          </div>
        </div>
      </aside>
    </>
  );
}
